import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Check, Mail, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * Reusable layout for partnership offer detail pages.
 * Props:
 * - testId, icon, badgeLabel, title, titleAccent, intro, sections, faq, ctaType
 */
export default function PartnershipDetailLayout({
  testId, icon: Icon, badgeLabel, title, titleAccent, intro, sections = [], faq = [], ctaType,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const goContact = () => navigate(`/partenariat?type=${ctaType}#contact`);

  return (
    <div className="min-h-screen bg-[#F9F9F7] font-body" data-testid={testId}>
      {/* Hero */}
      <section className="bg-[#1A3C34] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #C77D63 0%, transparent 50%), radial-gradient(circle at 80% 50%, #C77D63 0%, transparent 50%)' }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <Link to="/partenariat" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t('partnership.backToOffers')}
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C77D63]/20 border border-[#C77D63]/30 rounded-full text-sm font-semibold text-[#C77D63] mb-6">
            <Icon className="w-4 h-4" />
            {badgeLabel}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight">
            {title}
            <br />
            <span className="text-[#C77D63]">{titleAccent}</span>
          </h1>
          <p className="mt-6 text-lg text-white/60 max-w-2xl leading-relaxed">{intro}</p>
          <Button
            onClick={goContact}
            className="mt-8 h-12 px-8 bg-[#C77D63] text-white hover:bg-[#b06a51] rounded-lg font-bold shadow-lg shadow-[#C77D63]/25"
            data-testid="hero-cta"
          >
            {t('partnership.requestInfo')} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
          {sections.map((s, i) => (
            <div key={i}>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A3C34] tracking-tight mb-5">{s.title}</h2>
              {s.paragraphs?.map((p, k) => (
                <p key={k} className="text-base text-[#737373] leading-relaxed mb-3">{p}</p>
              ))}
              {s.items && (
                <ul className="space-y-2.5 mt-3">
                  {s.items.map((it, k) => (
                    <li key={k} className="flex items-start gap-2.5 text-base text-[#1A3C34]">
                      <Check className="w-5 h-5 text-[#C77D63] mt-0.5 flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="py-16 bg-white border-y border-[#E2E2DF]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A3C34] text-center tracking-tight mb-10">
              {t('partnership.faqTitle')}
            </h2>
            <div className="space-y-3">
              {faq.map((f, i) => (
                <details key={i} className="border border-[#E2E2DF] rounded-xl px-5 py-4 group hover:border-[#C77D63]/40 transition-colors">
                  <summary className="cursor-pointer font-heading text-base font-bold text-[#1A3C34] flex items-center justify-between gap-3">
                    {f.q}
                    <span className="text-[#C77D63] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-[#737373] leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-16 sm:py-20 bg-[#1A3C34]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">
            {t('partnership.readyTitle')}
          </h2>
          <p className="mt-4 text-base text-white/60">
            {t('partnership.readyDesc')}
          </p>
          <Button
            onClick={goContact}
            className="mt-8 h-12 px-8 bg-[#C77D63] text-white hover:bg-[#b06a51] rounded-lg font-bold shadow-lg shadow-[#C77D63]/25"
            data-testid="footer-cta"
          >
            {t('partnership.requestInfo')} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-white/50">
            <a href="mailto:info@flexi-location.be" className="flex items-center gap-2 hover:text-[#C77D63] transition-colors">
              <Mail className="w-4 h-4" /> info@flexi-location.be
            </a>
            <span className="hidden sm:block">|</span>
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4" /> 304 Chaussée de Stockel, 1200 Woluwe-Saint-Lambert
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
